import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { Popup, Button, PickerView } from 'antd-mobile';
import { useUnmount } from 'ahooks';
import { klona } from 'klona';
import { setMarkPainScreenshotUrl, useAIDiagnoseStore } from '../../store/useAIDiagnoseStore';
import { PAIN_LEVEL_2_TEXT, PART_2_TEXT } from '../pain-condition-level';

import cx from './index.module.scss';

interface Props {
  selectArea2Part: (keyof typeof PART_2_TEXT)[];
  currentEditField: keyof typeof PART_2_TEXT;
  setCurrentEditField: React.Dispatch<React.SetStateAction<keyof typeof PART_2_TEXT>>;
  isShow: boolean;
  setIsShow: Dispatch<SetStateAction<boolean>>;
  value: [AIDiagnose.PainLevel, number];
  onChange: (value: Props['value']) => void;
}

export const DURATION_COLUMN = [
  {
    value: 7,
    label: '1个周',
  },
  {
    value: 30,
    label: '1个月',
  },
  {
    value: 60,
    label: '2个月',
  },
  {
    value: 90,
    label: '3个月',
  },
];

export const PainLevelSelector: React.FC<Props> = ({
  isShow,
  selectArea2Part,
  currentEditField,
  setCurrentEditField,
  setIsShow,
  value,
  onChange,
}) => {
  const [innerPickerValue, setInnerPickerValue] = useState<Props['value']>(value || [undefined, undefined]);
  const markPainScreenshotUrl = useAIDiagnoseStore.use.markPainScreenshotUrl();

  const painColumn = useMemo(
    () =>
      Object.keys(PAIN_LEVEL_2_TEXT).map(item => ({
        value: +item,
        label: PAIN_LEVEL_2_TEXT[item],
      })),
    []
  );

  const selectArea2PartWithSort = useMemo(
    () =>
      klona(selectArea2Part)?.sort(
        (a, b) => ['shoulder', 'back', 'waist'].indexOf(a) - ['shoulder', 'back', 'waist'].indexOf(b)
      ),
    [selectArea2Part]
  );

  const currentPickIndex = useMemo(
    () => selectArea2PartWithSort?.findIndex(item => item === currentEditField),
    [currentEditField, selectArea2PartWithSort]
  );

  useUnmount(() => {
    if (markPainScreenshotUrl) {
      URL.revokeObjectURL(markPainScreenshotUrl);
      setMarkPainScreenshotUrl('');
    }
  });

  useEffect(() => {
    setInnerPickerValue(value);
  }, [value]);

  return (
    <Popup
      bodyClassName={cx('PainLevelSelector')}
      visible={isShow}
      destroyOnClose={true}
      onMaskClick={() => {
        setIsShow(false);
      }}
      onClose={() => {
        setIsShow(false);
      }}
    >
      <div className={cx('header')}>
        <div className={cx('header-title')}>{PART_2_TEXT[currentEditField]}疼痛情况</div>
        <div className={cx('img-wrapper')}>
          <div className={cx('img-box')}>
            <img src={markPainScreenshotUrl} alt="" className={cx('img', currentEditField)} />
          </div>
        </div>
      </div>
      <div className={cx('title')}>
        <div className={cx('item-wrapper')}>
          <div className={cx('title-item')}>您的疼痛程度</div>
          <div className={cx('title-item')}>疼痛持续时长</div>
        </div>
      </div>
      <div className={cx('content')}>
        <div className={cx('picker-view-item')}>
          <PickerView
            style={{ '--item-height': '60px' }}
            className={cx('picker-view')}
            columns={[painColumn, DURATION_COLUMN]}
            value={innerPickerValue}
            onChange={value => {
              setInnerPickerValue(value as Props['value']);
            }}
          />
        </div>
      </div>
      <div className={cx('footer')}>
        <Button
          className={cx('btn')}
          color="primary"
          onClick={() => {
            onChange?.(innerPickerValue);

            // 点击确认时，自动跳转到下一设置值中
            if (currentPickIndex < selectArea2PartWithSort?.length - 1) {
              setCurrentEditField(selectArea2PartWithSort[currentPickIndex + 1]);
            } else {
              setIsShow(false);
            }
          }}
        >
          确定
        </Button>
      </div>
    </Popup>
  );
};
